import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Avatar,
    Divider,
    Button,
    Grid,
    Stack,
    Chip,
    IconButton,
    CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconBell, IconChecks, IconTrash, IconSend } from '@tabler/icons';
import { useSocket } from 'contexts/SocketContext';
import { formatDistanceToNow } from 'date-fns';
import MainCard from 'ui-component/cards/MainCard';
import { useSelector } from 'react-redux';
import NotificationService from 'api/NotificationService';
import SendNotificationModal from 'views/admin/notifications/SendNotificationModal';

const NotificationPage = () => {
    const theme = useTheme();
    // userInfo from Redux to determine role
    const customization = useSelector((state) => state.customization);
    const userInfo = customization?.userInfo;
    const role = userInfo?.accountType === 'admin' ? 'admin' : 'vendor';

    // Socket state
    const { notifications: socketNotifications, markAllAsRead: markAllSocketRead } = useSocket();

    // Local state for API notifications
    const [apiNotifications, setApiNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendModalOpen, setSendModalOpen] = useState(false);

    // Fetch initial notifications
    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await NotificationService.getNotifications(role, { page: 1, limit: 50 });
            // Assuming response structure { data: [], ... } or direct array
            const data = response.data || response;
            setApiNotifications(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setLoading(false);
        }
    }, [role]);

    useEffect(() => {
        if (userInfo) {
            fetchNotifications();
        }
    }, [fetchNotifications, userInfo]);

    // Merge socket notifications with API notifications
    const displayNotifications = [...socketNotifications, ...apiNotifications];

    const handleMarkAsRead = async () => {
        try {
            await markAllSocketRead();
            fetchNotifications();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        try {
            await NotificationService.deleteNotification(role, id);
            setApiNotifications((prev) => prev.filter((n) => n._id !== id && n.id !== id));
        } catch (error) {
            console.error('Failed to delete', error);
        }
    };

    return (
        <MainCard
            title={
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Typography variant="h3">Notifications</Typography>
                    <Stack direction="row" spacing={1}>
                        {role === 'admin' && (
                            <Button
                                startIcon={<IconSend size={18} />}
                                onClick={() => setSendModalOpen(true)}
                                variant="contained"
                                color="secondary"
                                size="small"
                            >
                                Send Notification
                            </Button>
                        )}
                        <Button startIcon={<IconChecks size={18} />} onClick={handleMarkAsRead} variant="outlined" size="small">
                            Mark All as Read
                        </Button>
                    </Stack>
                </Stack>
            }
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress />
                </Box>
            ) : displayNotifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <IconBell size={64} stroke={1} color={theme.palette.grey[400]} />
                    <Typography variant="h4" color="textSecondary" sx={{ mt: 2 }}>
                        No notifications found
                    </Typography>
                </Box>
            ) : (
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {displayNotifications.map((notification, index) => (
                        <React.Fragment key={notification._id || notification.id || index}>
                            <ListItem
                                alignItems="flex-start"
                                sx={{ py: 2, bgcolor: notification.status === 'unread' ? theme.palette.primary.light : 'inherit' }}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => handleDelete(notification._id || notification.id)}
                                    >
                                        <IconTrash size={18} />
                                    </IconButton>
                                }
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            bgcolor: theme.palette.primary.light,
                                            color: theme.palette.primary.main
                                        }}
                                    >
                                        <IconBell size={20} />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {notification.title || 'Notification'}
                                        </Typography>
                                    }
                                    secondary={
                                        <React.Fragment>
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                                sx={{ display: 'block', my: 0.5 }}
                                            >
                                                {notification.message || notification.body}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {notification.createdAt
                                                    ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                                                    : 'Just now'}
                                            </Typography>
                                        </React.Fragment>
                                    }
                                />
                            </ListItem>
                            {index < displayNotifications.length - 1 && <Divider variant="inset" component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            )}

            {/* Admin Send Modal */}
            {role === 'admin' && (
                <SendNotificationModal
                    open={sendModalOpen}
                    onClose={() => setSendModalOpen(false)}
                    onSuccess={() => {
                        // Optionally fetch notifications to see your own broadcast if applicable
                    }}
                />
            )}
        </MainCard>
    );
};

export default NotificationPage;
